const INK = "#1A1A18";
const IVORY = "#FAFAF8";
const TEAL = "#4FB3A9";

const drawSpaced = (ctx, text, x, y, spacing) => {
  const total = ctx.measureText(text).width + spacing * (text.length - 1);
  let cx = x - total / 2;
  for (const ch of text) {
    ctx.fillText(ch, cx + ctx.measureText(ch).width / 2, y);
    cx += ctx.measureText(ch).width + spacing;
  }
};

export async function generateSurvivalCard(names) {
  await Promise.all([
    document.fonts.load('500 80px "Playfair Display"'),
    document.fonts.load('italic 500 40px "Playfair Display"'),
    document.fonts.load('500 30px "Outfit"'),
  ]);

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 640);
  glow.addColorStop(0, "rgba(27,133,123,0.16)");
  glow.addColorStop(1, "rgba(27,133,123,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  for (let i = 0; i < 5000; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.lineWidth = 1;
  ctx.strokeRect(44, 44, W - 88, H - 88);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = TEAL;
  ctx.font = '500 26px "Outfit", sans-serif';
  drawSpaced(ctx, "MY SURVIVAL MECHANISMS", W / 2, 180, 8);

  ctx.strokeStyle = "rgba(79,179,169,0.6)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, 216);
  ctx.lineTo(W / 2 + 36, 216);
  ctx.stroke();

  const upper = names.map((n) => n.toUpperCase());
  let fontSize = 82;
  const maxWidth = 880;
  ctx.font = `500 ${fontSize}px "Playfair Display", serif`;
  const widest = () => Math.max(...upper.map((w) => ctx.measureText(w).width));
  while (widest() > maxWidth && fontSize > 34) {
    fontSize -= 3;
    ctx.font = `500 ${fontSize}px "Playfair Display", serif`;
  }
  const slot = 220;
  const blockH = slot * (upper.length - 1);
  const startY = H / 2 - blockH / 2 - 20;

  upper.forEach((w, i) => {
    const y = startY + i * slot;
    ctx.fillStyle = IVORY;
    ctx.font = `500 ${fontSize}px "Playfair Display", serif`;
    ctx.fillText(w, W / 2, y);
    if (i < upper.length - 1) {
      ctx.strokeStyle = "rgba(79,179,169,0.5)";
      ctx.beginPath();
      ctx.moveTo(W / 2 - 28, y + slot / 2);
      ctx.lineTo(W / 2 + 28, y + slot / 2);
      ctx.stroke();
    }
  });

  ctx.fillStyle = "rgba(250,250,248,0.75)";
  ctx.font = 'italic 500 38px "Playfair Display", serif';
  ctx.fillText("Not who I am. Who takes over.", W / 2, H - 190);

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, H - 148);
  ctx.lineTo(W / 2 + 36, H - 148);
  ctx.stroke();

  ctx.fillStyle = TEAL;
  ctx.font = '500 22px "Outfit", sans-serif';
  drawSpaced(ctx, "CREATING WEALTH THROUGH COMMUNITY", W / 2, H - 100, 6);

  return canvas.toDataURL("image/png");
}

const wrapText = (ctx, text, maxWidth) => {
  const words = text.split(" ");
  const lines = [];
  let line = "";
  for (const w of words) {
    const test = line ? `${line} ${w}` : w;
    if (ctx.measureText(test).width > maxWidth && line) {
      lines.push(line);
      line = w;
    } else {
      line = test;
    }
  }
  if (line) lines.push(line);
  return lines;
};

export async function generateFoundationCard(essenceWords, mechanisms, superpower) {
  await Promise.all([
    document.fonts.load('500 60px "Playfair Display"'),
    document.fonts.load('italic 500 56px "Playfair Display"'),
    document.fonts.load('500 30px "Outfit"'),
  ]);

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 640);
  glow.addColorStop(0, "rgba(27,133,123,0.16)");
  glow.addColorStop(1, "rgba(27,133,123,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  for (let i = 0; i < 5000; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.lineWidth = 1;
  ctx.strokeRect(44, 44, W - 88, H - 88);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";
  const maxWidth = 860;

  ctx.fillStyle = TEAL;
  ctx.font = '500 26px "Outfit", sans-serif';
  drawSpaced(ctx, "MY UNMISTAKABLE FOUNDATION", W / 2, 160, 7);
  ctx.strokeStyle = "rgba(79,179,169,0.6)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, 196);
  ctx.lineTo(W / 2 + 36, 196);
  ctx.stroke();

  const label = (text, y) => {
    ctx.fillStyle = "rgba(250,250,248,0.5)";
    ctx.font = '500 20px "Outfit", sans-serif';
    drawSpaced(ctx, text, W / 2, y, 6);
  };
  const divider = (y) => {
    ctx.strokeStyle = "rgba(250,250,248,0.12)";
    ctx.beginPath();
    ctx.moveTo(W / 2 - 200, y);
    ctx.lineTo(W / 2 + 200, y);
    ctx.stroke();
  };

  label("ESSENCE", 300);
  const essenceLine = essenceWords.map((w) => w.toUpperCase()).join(" · ");
  let fs = 52;
  ctx.font = `500 ${fs}px "Playfair Display", serif`;
  while (ctx.measureText(essenceLine).width > maxWidth && fs > 26) {
    fs -= 2;
    ctx.font = `500 ${fs}px "Playfair Display", serif`;
  }
  ctx.fillStyle = IVORY;
  ctx.fillText(essenceLine, W / 2, 370);

  divider(440);

  label("SURVIVAL MECHANISMS", 520);
  fs = 46;
  ctx.font = `500 ${fs}px "Playfair Display", serif`;
  const widestMech = () => Math.max(...mechanisms.map((m) => ctx.measureText(m.toUpperCase()).width));
  while (widestMech() > maxWidth && fs > 26) {
    fs -= 2;
    ctx.font = `500 ${fs}px "Playfair Display", serif`;
  }
  ctx.fillStyle = "rgba(250,250,248,0.82)";
  mechanisms.forEach((m, i) => {
    ctx.fillText(m.toUpperCase(), W / 2, 590 + i * (fs * 1.45));
  });

  divider(850);

  label("SUPERPOWER", 930);
  fs = 56;
  ctx.font = `italic 500 ${fs}px "Playfair Display", serif`;
  let lines = wrapText(ctx, superpower, maxWidth);
  while (lines.length > 3 && fs > 30) {
    fs -= 3;
    ctx.font = `italic 500 ${fs}px "Playfair Display", serif`;
    lines = wrapText(ctx, superpower, maxWidth);
  }
  ctx.fillStyle = TEAL;
  lines.forEach((l, i) => {
    ctx.fillText(l, W / 2, 1010 + i * (fs * 1.25));
  });

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, H - 148);
  ctx.lineTo(W / 2 + 36, H - 148);
  ctx.stroke();

  ctx.fillStyle = "rgba(250,250,248,0.6)";
  ctx.font = '500 22px "Outfit", sans-serif';
  drawSpaced(ctx, "CREATING WEALTH THROUGH COMMUNITY", W / 2, H - 100, 6);

  return canvas.toDataURL("image/png");
}

export async function generateEssenceCard(words) {
  await Promise.all([
    document.fonts.load('500 130px "Playfair Display"'),
    document.fonts.load('italic 500 40px "Playfair Display"'),
    document.fonts.load('500 30px "Outfit"'),
  ]);

  const W = 1080;
  const H = 1350;
  const canvas = document.createElement("canvas");
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = INK;
  ctx.fillRect(0, 0, W, H);

  const glow = ctx.createRadialGradient(W / 2, H / 2, 80, W / 2, H / 2, 640);
  glow.addColorStop(0, "rgba(27,133,123,0.16)");
  glow.addColorStop(1, "rgba(27,133,123,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(0, 0, W, H);

  for (let i = 0; i < 5000; i++) {
    ctx.fillStyle = `rgba(255,255,255,${Math.random() * 0.03})`;
    ctx.fillRect(Math.random() * W, Math.random() * H, 1, 1);
  }

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.lineWidth = 1;
  ctx.strokeRect(44, 44, W - 88, H - 88);

  ctx.textAlign = "center";
  ctx.textBaseline = "alphabetic";

  ctx.fillStyle = TEAL;
  ctx.font = '500 26px "Outfit", sans-serif';
  drawSpaced(ctx, "YOUR ESSENCE", W / 2, 180, 10);

  ctx.strokeStyle = "rgba(79,179,169,0.6)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, 216);
  ctx.lineTo(W / 2 + 36, 216);
  ctx.stroke();

  const upper = words.map((w) => w.toUpperCase());
  let fontSize = 128;
  const maxWidth = 880;
  ctx.font = `500 ${fontSize}px "Playfair Display", serif`;
  const widest = () => Math.max(...upper.map((w) => ctx.measureText(w).width));
  while (widest() > maxWidth && fontSize > 56) {
    fontSize -= 4;
    ctx.font = `500 ${fontSize}px "Playfair Display", serif`;
  }
  const lineGap = fontSize * 1.32;
  const blockH = lineGap * (upper.length - 1);
  const startY = (H - 240) / 2 - blockH / 2 + fontSize * 0.36 + 90;

  ctx.fillStyle = IVORY;
  upper.forEach((w, i) => {
    ctx.fillText(w, W / 2, startY + i * lineGap);
  });

  ctx.fillStyle = "rgba(250,250,248,0.75)";
  ctx.font = 'italic 500 38px "Playfair Display", serif';
  ctx.fillText("Who you've been all along.", W / 2, H - 190);

  ctx.strokeStyle = "rgba(250,250,248,0.14)";
  ctx.beginPath();
  ctx.moveTo(W / 2 - 36, H - 148);
  ctx.lineTo(W / 2 + 36, H - 148);
  ctx.stroke();

  ctx.fillStyle = TEAL;
  ctx.font = '500 22px "Outfit", sans-serif';
  drawSpaced(ctx, "CREATING WEALTH THROUGH COMMUNITY", W / 2, H - 100, 6);

  return canvas.toDataURL("image/png");
}
